import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, ShieldCheck, Globe, Copy, Check, Trash2, AlertTriangle, Info } from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

const UNCHANGED_KEY = "__UNCHANGED__";
const MASK_DISPLAY = "••••••••••••••••••••";

const formatDate = (unix) =>
  unix ? new Date(unix * 1000).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";

// Instagram with the client's OWN Meta app (the earlier way): token + App Secret here, the webhook address and verify
// token shown below go into that app's Webhooks settings. Messages come straight to this server (no relay).
const InstagramOwnAppConfig = ({ activeAiAgentId }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [config, setConfig] = useState(null);
  const [token, setToken] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      let response = await ApiClient.request("/instagram/config", { aiAgentId: activeAiAgentId });
      const c = response.config;
      const kambaaApp = c?.ig_user_id && c?.setup !== "manual";
      if (response.success && !kambaaApp && !c?.webhook_url) {
        // the webhook address + verify token exist before connecting, so the Meta app can be set up first
        const prepared = await ApiClient.request("/instagram/manual-prepare", { aiAgentId: activeAiAgentId, method: "POST" });
        if (prepared.success) response = { ...response, config: prepared.config };
      }
      if (response.success) {
        setConfig(response.config);
        setToken(response.config?.access_token ? UNCHANGED_KEY : "");
        setAppSecret(response.config?.app_secret ? UNCHANGED_KEY : "");
      }
    } catch (error) {
      console.log("Instagram config not found");
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const isManual = config?.setup === "manual";
  const otherSetup = Boolean(config?.ig_user_id) && !isManual;   // connected with "Connect with Instagram"
  const connected = isManual && Boolean(config?.ig_user_id);
  const disconnected = connected && config?.connection_status === "disconnected";
  const tokenChanged = token.trim() && token !== UNCHANGED_KEY;
  const secretChanged = appSecret.trim() && appSecret !== UNCHANGED_KEY;

  const saveConfig = async () => {
    if (!connected && (!tokenChanged || !appSecret.trim())) {
      showToast("Please enter the access token and the App Secret", "error");
      return;
    }
    try {
      setSaving(true);
      const response = await ApiClient.request("/instagram/manual-config", {
        aiAgentId: activeAiAgentId,
        method: "PUT",
        body: JSON.stringify({
          access_token: tokenChanged ? token.trim() : UNCHANGED_KEY,
          app_secret: secretChanged ? appSecret.trim() : UNCHANGED_KEY,
        }),
      });
      if (response.success) showToast(response.message || "Instagram saved", "success");
    } catch (error) {
      // Error handled by ApiClient global toast
    } finally {
      setSaving(false);
      loadConfig();
    }
  };

  const toggleActive = async () => {
    if (!connected) {
      showToast("Please connect an Instagram account first", "error");
      return;
    }
    try {
      const response = await ApiClient.request("/instagram/manual-config", {
        aiAgentId: activeAiAgentId,
        method: "PUT",
        body: JSON.stringify({ is_active: !config.is_active }),
      });
      if (response.success) {
        setConfig(response.config);
        showToast(response.active ? "Instagram activated" : "Instagram deactivated", "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    }
  };

  const deleteConfig = async () => {
    try {
      setSaving(true);
      const response = await ApiClient.request("/ai-agents/integrations/instagram", { method: "DELETE" });
      if (response.success) {
        setConfig(null);
        setToken("");
        setAppSecret("");
        showToast("Instagram disconnected", "success");
      }
    } catch (error) {
      // handled globally
    } finally {
      setSaving(false);
      setIsDeleteModalOpen(false);
    }
  };

  const copy = async (what, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(what);
      showToast("Copied to clipboard!", "success");
      setTimeout(() => setCopied(""), 2000);
    } catch (error) {
      showToast("Failed to copy", "error");
    }
  };

  const copyRow = (what, label, value) => (
    <div>
      <p className="text-[11px] font-medium text-zinc-700 mb-1">{label}</p>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-1.5 sm:p-2 bg-zinc-50 rounded-lg border border-zinc-200">
        <div className="font-mono text-[11px] sm:text-xs text-zinc-700 px-2 py-1 flex-1 break-all select-all min-w-0">{value}</div>
        <button
          type="button"
          onClick={() => copy(what, value)}
          className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex-shrink-0 ${
            copied === what ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-white text-zinc-800 border border-zinc-200 hover:bg-zinc-100"
          }`}
        >
          {copied === what ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
          <span>{copied === what ? "Copied" : "Copy"}</span>
        </button>
      </div>
    </div>
  );

  const secretInput = (label, value, setValue, placeholder) => (
    <div>
      <label className="block text-xs font-medium text-zinc-700 mb-1.5">{label}</label>
      <input
        type="password"
        value={value === UNCHANGED_KEY ? MASK_DISPLAY : value}
        onChange={(e) => setValue(e.target.value)}
        onFocus={() => {
          if (value === UNCHANGED_KEY) setValue("");
        }}
        placeholder={value === UNCHANGED_KEY ? MASK_DISPLAY : placeholder}
        disabled={otherSetup}
        className="w-full px-3.5 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 font-mono transition-colors disabled:opacity-50"
      />
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-4 sm:space-y-6">

        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 sm:pb-5 border-b border-zinc-100 gap-3 sm:gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/integrations")}
              className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors"
              title="Back to Integrations"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">Instagram (own Meta app)</h1>
              <p className="text-xs text-zinc-500 mt-0.5">
                Answer Instagram direct messages using your own Meta developer app.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2.5 self-start sm:self-auto pl-9 sm:pl-0">
            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide uppercase border ${
              connected && config?.is_active && !disconnected
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-zinc-50 text-zinc-500 border-zinc-200"
            }`}>
              {connected && config?.is_active ? (disconnected ? "Disconnected" : "Active") : "Disabled"}
            </span>
            <button
              type="button"
              onClick={toggleActive}
              className={`w-8 h-4.5 rounded-full transition-colors relative flex-shrink-0 ${connected && config?.is_active ? "bg-zinc-900" : "bg-zinc-200"}`}
              title={connected && config?.is_active ? "Deactivate integration" : "Activate integration"}
            >
              <div className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full transition-transform ${connected && config?.is_active ? "left-4" : "left-0.5"}`} />
            </button>
          </div>
        </div>

        {otherSetup && (
          <div className="flex items-start gap-2.5 p-3.5 rounded-lg border border-amber-200 bg-amber-50 text-amber-800">
            <AlertTriangle size={15} className="shrink-0 mt-0.5" />
            <p className="text-xs">
              This AI agent is already connected to @{config.username || config.ig_user_id} with Connect with Instagram.
              Disconnect it under Integrations → Instagram first to use your own Meta app.
            </p>
          </div>
        )}
        {disconnected && (
          <div className="flex items-start gap-2.5 p-3.5 rounded-lg border border-red-200 bg-red-50 text-red-700">
            <AlertTriangle size={15} className="shrink-0 mt-0.5" />
            <p className="text-xs">
              Instagram is not connected: {config.connection_error || "access was removed"}. Paste a new access token below.
            </p>
          </div>
        )}

        <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-5">
          <div className="flex items-center gap-2.5 pb-3 border-b border-zinc-100">
            <ShieldCheck className="text-zinc-800 shrink-0" size={18} />
            <div>
              <h2 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Your Meta App</h2>
              <p className="text-[11px] text-zinc-500">Instagram API with Instagram login, in your own Meta developer account</p>
            </div>
          </div>

          {connected && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-3 bg-zinc-50 rounded-lg border border-zinc-200">
                <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Account</p>
                <p className="text-xs font-semibold text-zinc-900 mt-1">@{config.username || "unknown"}</p>
                <p className="text-[10px] text-zinc-400 font-mono mt-0.5">{config.ig_user_id}</p>
              </div>
              <div className="p-3 bg-zinc-50 rounded-lg border border-zinc-200">
                <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Status</p>
                <p className={`text-xs font-semibold mt-1 ${disconnected ? "text-red-600" : "text-emerald-700"}`}>
                  {disconnected ? "Disconnected" : "Connected"}
                </p>
              </div>
              <div className="p-3 bg-zinc-50 rounded-lg border border-zinc-200">
                <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Access token</p>
                <p className="text-xs font-semibold text-zinc-900 mt-1">Renewed automatically</p>
                {config.token_expires_at && <p className="text-[10px] text-zinc-400 mt-0.5">valid until {formatDate(config.token_expires_at)}</p>}
              </div>
            </div>
          )}

          {secretInput(connected ? "Replace access token" : "Instagram access token", token, setToken, "IGAA...")}
          {secretInput("App Secret", appSecret, setAppSecret, "Instagram App Secret of your Meta app")}
          <p className="text-[11px] text-zinc-400 -mt-3">
            Both from your Meta app → Instagram → API setup with Instagram login (Generate access tokens; App secret).
            The App Secret checks that messages really come from Meta. Both are stored encrypted.
          </p>

          <div className="pt-3 border-t border-zinc-100 flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-2.5">
            {connected ? (
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(true)}
                disabled={saving}
                className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-40"
              >
                <Trash2 size={14} />
                <span>Disconnect Instagram</span>
              </button>
            ) : <div />}
            <button
              type="button"
              onClick={saveConfig}
              disabled={saving || otherSetup || (!tokenChanged && !secretChanged)}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-40 text-xs font-medium transition-colors"
            >
              {saving ? <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save size={14} />}
              <span>{saving ? "Saving..." : connected ? "Save" : "Connect Instagram"}</span>
            </button>
          </div>
        </div>

        {isManual && config.webhook_url && (
          <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-3">
            <div className="flex items-center gap-2.5 pb-3 border-b border-zinc-100">
              <Globe className="text-zinc-800 shrink-0" size={18} />
              <div>
                <h3 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Webhook for your Meta app</h3>
                <p className="text-[11px] text-zinc-500">
                  Your Meta app → Instagram → API setup with Instagram login → Webhooks: enter both, then subscribe the field "messages"
                </p>
              </div>
            </div>
            {copyRow("url", "Callback URL", config.webhook_url)}
            {copyRow("verify", "Verify token", config.verify_token)}
            <p className="text-[11px] text-zinc-400">
              These stay the same for this AI agent (you can enter them in Meta before or after connecting). Meta's
              "Verify and save" works right away; messages are answered once the account is connected above.
              Disconnecting creates a new address next time.
            </p>
          </div>
        )}

        <div className="flex items-start gap-2 p-3 bg-zinc-50 rounded-lg border border-zinc-200">
          <Info size={13} className="text-zinc-400 shrink-0 mt-0.5" />
          <p className="text-[11px] text-zinc-500">
            Prefer the simpler way? Integrations → Instagram connects with one click through Kambaa's app. Use this box
            only if you run your own Meta app. Either way the AI agent answers text messages, Live Chat replies go to
            Instagram (within 24 hours of the customer's last message) and the token is renewed automatically.
          </p>
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={deleteConfig}
        title="Disconnect Instagram?"
        message="The AI agent stops answering this Instagram account's direct messages. Remove the webhook from your Meta app as well."
        confirmText="Disconnect"
        isLoading={saving}
      />
    </div>
  );
};

export default InstagramOwnAppConfig;
